import { createApp, shallowRef } from "vue";
import { icon, Marker, marker, Point } from "leaflet";

import { currentLocation, currentOpenedPopup, leafletElement } from ".";
import { courierData } from "@client/elements/repository";

import Popup from "@client/components/Popup.vue";

export const markers = shallowRef<Marker<unknown>[]>([]);

// Function to create icon while preserving aspect ratio
function createIconWithAspectRatio(iconUrl: string, size: number): L.Icon {
	// Create a temporary image to get the natural dimensions
	const img = new Image();
	img.src = iconUrl;

	// Default dimensions to use until the image loads
	let width = size;
	let height = size;

	// If the image is already loaded (from cache)
	if (img.complete && img.naturalWidth > 0) {
		const aspectRatio = img.naturalWidth / img.naturalHeight;
		width = aspectRatio > 1 ? size : size * aspectRatio;
		height = aspectRatio > 1 ? size / aspectRatio : size;
	} else {
		// Set the dimensions when the image loads
		img.onload = () => {
			const aspectRatio = img.naturalWidth / img.naturalHeight;
			width = aspectRatio > 1 ? size : size * aspectRatio;
			height = aspectRatio > 1 ? size / aspectRatio : size;
		};
	}

	return icon({
		iconUrl,
		iconSize: new Point(width, height),
		iconAnchor: [width / 2, height],
		popupAnchor: [0, -height]
	});
}

export async function openMarkerPopupById(locationId: string) {
	const marker = markers.value.find((m: any) => {
		const contentId = JSON.parse(m._popup.getContent().dataset.id).id;
		return currentLocation.value?.id === contentId;
	});

	if (marker) {
		currentOpenedPopup.value?.close();
		marker.openPopup();
		leafletElement.value?.panTo(marker.getLatLng());
		currentOpenedPopup.value = marker.getPopup();
	}
}

export function addMarkers() {
	if (!courierData.value || courierData.value.length === 0) {
		console.error("No courier data available for markers.");
		return;
	}

	if (!leafletElement.value) return;

	if (markers.value && markers.value.length > 0) {
		markers.value?.forEach((mark) => mark?.remove());
		markers.value = [];
	}

	markers.value = courierData.value?.flatMap((courier) =>
		courier?.locations
			?.map((location) => {
				const customIcon = createIconWithAspectRatio(courier.courier.images.mark, 28);

				const mark = marker([location.geo.latitude, location.geo.longitude], {
					icon: customIcon,
					title: location.name,
					riseOnHover: true
				});

				const popupDiv = document.createElement("div");

				popupDiv.dataset.id = JSON.stringify(location);
				popupDiv.dataset.icon = courier.courier.images.mark;
				popupDiv.dataset.courier = courier.courier.name;

				const popupApp = createApp(Popup, {
					location,
					icon: courier.courier.images.mark,
					courier: courier.courier.name
				});

				popupApp.mount(popupDiv);

				mark.bindPopup(popupDiv);
				mark.addTo(leafletElement.value as any);

				return mark;
			})
			.filter((mark) => mark !== undefined)
	);
}
